import { useEffect, useId, useState } from "react";
import {
  Bell,
  Building2,
  Calendar,
  Clock,
  Loader2,
  MapPin,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRequiredFields } from "@/hooks/useRequiredFields";
import { describeApiError, type FriendlyError } from "@/lib/apiErrorMessages";
import { FieldError, MissingFieldsSummary, SubmitErrorNotice } from "@/components/common/FormFeedback";
import { useCreateInteraction } from "@/hooks/useInteractions";
import { useAccountContacts } from "@/hooks/useContacts";
import { AccountInlinePicker } from "@/components/account/AccountInlinePicker";
import {
  composePlannedAt,
  DURATION_CHOICES,
  INTERACTION_TYPES,
  REMINDER_CHOICES,
} from "./constants";
import type {
  InteractionCreatePayload,
  InteractionCreateResponse,
  InteractionType,
} from "@/api/types";

type EntryPoint = "account" | "today" | "global";

interface PrefilledAccount {
  id: string;
  name: string;
  /** Optional primary-contact pin for the "preselect primary" rule. */
  primaryContactId?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Required when entryPoint="account" or "today"; optional for global (user picks). */
  account?: PrefilledAccount | null;
  /** Locks the account selector for entryPoint="account" (Account 360). */
  entryPoint: EntryPoint;
  onCreated?: (created: InteractionCreateResponse) => void;
}

export function CreateInteractionDrawer({
  open,
  onClose,
  account,
  entryPoint,
  onCreated,
}: Props) {
  const panelRef = useDrawerA11y(open, () => onClose());
  const headingId = useId();
  const typeId = useId();
  const dateId = useId();
  const timeId = useId();
  const topicId = useId();
  const locationId = useId();

  const [type, setType] = useState<InteractionType>("meeting");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState<number>(60);
  const [topic, setTopic] = useState("");
  const [location, setLocation] = useState("");
  const [reminder, setReminder] = useState<"15m" | "1h" | "1d" | "none">("1h");
  const [contactId, setContactId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<FriendlyError | null>(null);

  // For global entry — account search inside the drawer.
  const [pickedAccount, setPickedAccount] = useState<PrefilledAccount | null>(null);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);

  const accountLocked = entryPoint === "account" && !!account;
  const effectiveAccount = account ?? pickedAccount;

  const mutation = useCreateInteraction();
  const contactsQ = useAccountContacts(effectiveAccount?.id, 50);

  const contactRequired = type === "call";
  // §0 — «Запланувати» never disabled for gaps: press → list + jump to the gap.
  const req = useRequiredFields([
    {
      key: "account",
      label: "Клієнт",
      missing: !effectiveAccount,
      message: accountSearch.trim() ? "Оберіть клієнта зі списку" : "Оберіть клієнта",
    },
    { key: "date", label: "Дата", missing: !date, message: "Вкажіть дату" },
    { key: "time", label: "Час", missing: !time, message: "Вкажіть час" },
    {
      key: "contact",
      label: "Контакт",
      missing: contactRequired && !contactId,
      message: "Для дзвінка контакт обов'язковий",
    },
    { key: "topic", label: "Тема", missing: !topic.trim(), message: "Вкажіть тему" },
  ]);
  const resetRequired = req.reset;

  // Preselect primary contact when account becomes known.
  useEffect(() => {
    if (!effectiveAccount) {
      setContactId(null);
      return;
    }
    if (contactId) return;
    if (effectiveAccount.primaryContactId) {
      setContactId(effectiveAccount.primaryContactId);
      return;
    }
    const items = contactsQ.data?.items ?? [];
    const primary = items.find((c) => c.isPrimary && !c._redacted);
    if (primary) setContactId(primary.id);
  }, [effectiveAccount, contactsQ.data, contactId]);

  // Reset when closed so a re-open starts fresh.
  useEffect(() => {
    if (open) return;
    setType("meeting");
    setDate("");
    setTime("");
    setDuration(60);
    setTopic("");
    setLocation("");
    setReminder("1h");
    setContactId(null);
    setSubmitError(null);
    resetRequired();
    setPickedAccount(null);
    setAccountSearch("");
    setAccountPickerOpen(false);
  }, [open, resetRequired]);

  if (!open) return null;

  const visibleContacts = (contactsQ.data?.items ?? []).filter((c) => !c._redacted);

  const titleByType: Record<InteractionType, string> = {
    meeting: "Запланувати зустріч",
    call: "Запланувати дзвінок",
    video: "Запланувати відеозустріч",
  };

  const submit = async () => {
    setSubmitError(null);
    if (!req.validate() || !effectiveAccount) return;

    const payload: InteractionCreatePayload = {
      type,
      accountId: effectiveAccount.id,
      contactId: contactId ?? null,
      plannedAt: composePlannedAt(date, time),
      durationMinutes: duration,
      topic: topic.trim(),
      location: type === "meeting" && location.trim() ? location.trim() : null,
      reminder: { type: "crm_only", value: reminder },
    };

    try {
      const res = await mutation.mutateAsync(payload);
      onCreated?.(res);
      onClose();
    } catch (err) {
      setSubmitError(describeApiError(err, "Не вдалося запланувати"));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[480px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">
            {titleByType[type]}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити форму"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Context chip */}
        {effectiveAccount && (
          <div className="border-b bg-muted/40 px-5 py-3">
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-muted-foreground" aria-hidden />
              <span className="font-medium truncate">{effectiveAccount.name}</span>
              {accountLocked && (
                <span className="text-xs text-muted-foreground">· prefilled</span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-5 px-5 py-5">
          {/* Global entry — account picker */}
          {!accountLocked && (
            <div ref={req.fieldRef("account")}>
              <label className="mb-1.5 block text-xs font-medium">
                Клієнт <span className="text-destructive">*</span>
              </label>
              {effectiveAccount ? (
                <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 truncate">
                    <Building2 className="w-4 h-4 text-muted-foreground" aria-hidden />
                    {effectiveAccount.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPickedAccount(null);
                      setContactId(null);
                      setAccountPickerOpen(true);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Змінити
                  </button>
                </div>
              ) : (
                <AccountInlinePicker
                  query={accountSearch}
                  onQueryChange={setAccountSearch}
                  isOpen={accountPickerOpen || accountSearch.length >= 2}
                  onOpen={() => setAccountPickerOpen(true)}
                  onPick={(a) => {
                    setPickedAccount(a);
                    setAccountSearch("");
                    setAccountPickerOpen(false);
                  }}
                  error={req.errorFor("account")}
                  errorId={req.errorId("account")}
                />
              )}
            </div>
          )}

          {/* Type */}
          <div>
            <label htmlFor={typeId} className="mb-1.5 block text-xs font-medium">
              Тип
            </label>
            <div id={typeId} className="flex gap-2" role="radiogroup" aria-label="Тип активності">
              {INTERACTION_TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setType(t.id)}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 text-sm font-medium transition-colors",
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon className="w-4 h-4" aria-hidden /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div ref={req.fieldRef("date")}>
              <label htmlFor={dateId} className="mb-1.5 block text-xs font-medium">
                Дата <span className="text-destructive">*</span>
              </label>
              <div
                className={cn(
                  "flex items-center gap-2 rounded-md border px-2.5 py-1.5",
                  req.errorFor("date") && "border-destructive",
                )}
              >
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                <input
                  id={dateId}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  {...req.invalidProps("date")}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </div>
              <FieldError id={req.errorId("date")} message={req.errorFor("date")} />
            </div>
            <div ref={req.fieldRef("time")}>
              <label htmlFor={timeId} className="mb-1.5 block text-xs font-medium">
                Час <span className="text-destructive">*</span>
              </label>
              <div
                className={cn(
                  "flex items-center gap-2 rounded-md border px-2.5 py-1.5",
                  req.errorFor("time") && "border-destructive",
                )}
              >
                <Clock className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                <input
                  id={timeId}
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  {...req.invalidProps("time")}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </div>
              <FieldError id={req.errorId("time")} message={req.errorFor("time")} />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="mb-1.5 block text-xs font-medium">Тривалість</label>
            <div className="flex gap-1.5">
              {DURATION_CHOICES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  className={cn(
                    "flex-1 rounded-md border py-1.5 text-xs font-medium transition-colors",
                    duration === d
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {d} хв
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div ref={req.fieldRef("contact")}>
            <label className="mb-1.5 block text-xs font-medium">
              Контакт{" "}
              {contactRequired ? (
                <span className="text-destructive">*</span>
              ) : (
                <span className="text-muted-foreground font-normal">(рекомендовано)</span>
              )}
            </label>
            {!effectiveAccount ? (
              <p className="text-xs text-muted-foreground">
                Спочатку оберіть клієнта.
              </p>
            ) : contactsQ.isPending ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Завантаження…
              </div>
            ) : visibleContacts.length === 0 ? (
              <p className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Немає доступних контактів. Продовжте без контакту.
              </p>
            ) : (
              <select
                value={contactId ?? ""}
                onChange={(e) => setContactId(e.target.value || null)}
                {...req.invalidProps("contact")}
                className={cn(
                  "flex h-9 w-full rounded-md border bg-background px-3 text-sm",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  req.errorFor("contact") && "border-destructive",
                )}
              >
                <option value="">{contactRequired ? "Оберіть контакт…" : "Без контакту"}</option>
                {visibleContacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.isPrimary ? "· основний" : ""}
                  </option>
                ))}
              </select>
            )}
            <FieldError id={req.errorId("contact")} message={req.errorFor("contact")} />
          </div>

          {/* Topic */}
          <div ref={req.fieldRef("topic")}>
            <label htmlFor={topicId} className="mb-1.5 block text-xs font-medium">
              Тема / порядок денний <span className="text-destructive">*</span>
            </label>
            <textarea
              id={topicId}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={2}
              placeholder="Про що зустріч?"
              {...req.invalidProps("topic")}
              className={cn(
                "w-full rounded-md border px-3 py-2 text-sm",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                req.errorFor("topic") && "border-destructive",
              )}
            />
            <FieldError id={req.errorId("topic")} message={req.errorFor("topic")} />
          </div>

          {/* Location (meeting only) */}
          {type === "meeting" && (
            <div>
              <label htmlFor={locationId} className="mb-1.5 block text-xs font-medium">
                Місце <span className="text-muted-foreground">(опціонально)</span>
              </label>
              <div className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                <Input
                  id={locationId}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Адреса / офіс"
                  className="border-0 px-0 py-0 h-7 focus-visible:ring-0"
                />
              </div>
            </div>
          )}

          {/* CRM reminder */}
          <div>
            <label className="mb-1.5 block text-xs font-medium">CRM-нагадування</label>
            <div className="flex flex-wrap gap-1.5">
              {REMINDER_CHOICES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setReminder(r.id)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors",
                    reminder === r.id
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {r.id !== "none" && <Bell className="w-3 h-3" aria-hidden />} {r.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Calendar sync — у наступній фазі
            </p>
          </div>

        </div>

        <div className="sticky bottom-0 border-t bg-background px-5 py-4">
          <MissingFieldsSummary missing={req.missing} onJump={req.focusField} />
          <SubmitErrorNotice error={submitError} />
          <Button
            type="button"
            onClick={submit}
            disabled={mutation.isPending}
            className="w-full h-11 text-sm font-semibold"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Створення…
              </>
            ) : (
              "Запланувати"
            )}
          </Button>
          <p className="mt-1.5 text-center text-xs text-muted-foreground">
            Створює зустріч у CRM · Calendar sync — у наступній фазі
          </p>
        </div>
      </div>
    </div>
  );
}

